import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { isB2BUser } from "../lib/roles";
import FavoriteButton from "./FavoriteButton";
import ProductZoom from "./ProductZoom";

export default function WineCard({ wine, compact }) {
  const { user } = useAuth();
  const canSeePallet = isB2BUser(user);
  return (
    <div
      data-testid={`wine-card-${wine.id}`}
      className="group bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm overflow-hidden hover-lift relative"
    >
      <div className="absolute top-3 right-3 z-20">
        <FavoriteButton productType="wine" productId={wine.id} meta={{ name: wine.name, image: wine.image, price: wine.price_per_bottle }} />
      </div>
      <div className="relative aspect-[4/5] bg-white">
        <ProductZoom
          src={wine.image}
          alt={`${wine.name} ${wine.vintage}`}
          imgClassName="scale-[1.05]"
        />
        <Link
          to={`/b2b/wines/${wine.id}`}
          data-testid={`wine-detail-link-${wine.id}`}
          className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/70 to-transparent flex items-end justify-center pb-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
        >
          Vedi scheda →
        </Link>
        <div className="absolute top-3 left-3 chip bg-black/80 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30 backdrop-blur pointer-events-none">
          {wine.denomination}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <Link to={`/b2b/wines/${wine.id}`} className="block">
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--accent))]">{wine.winery}</div>
          <h3 className="editorial-heading text-xl leading-tight text-[hsl(var(--foreground))]">{wine.name}</h3>
          <div className="text-sm text-[hsl(var(--muted-foreground))]">{wine.vintage} · {wine.grape}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{wine.region} · {wine.country || "Italia"}</div>
        </Link>
        {!compact && (
          <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-[hsl(var(--border))]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Btl</div>
              <div className="font-bold text-[hsl(var(--accent))]">€{wine.price_per_bottle.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cartone</div>
              <div className="font-bold text-[hsl(var(--accent))]">€{wine.price_per_case.toFixed(0)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pallet</div>
              {canSeePallet ? (
                <div className="font-bold text-[hsl(var(--accent))]">€{(wine.price_per_pallet/1000).toFixed(1)}k</div>
              ) : (
                <Link to="/login" data-testid={`wine-pallet-locked-${wine.id}`} className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]">
                  <Lock className="h-3 w-3" />B2B login
                </Link>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between text-xs pt-1">
          <span className={`chip ${wine.stock_available > 1000 ? "bg-green-900/40 text-green-300" : "bg-amber-900/40 text-amber-300"}`}>
            Stock: {wine.stock_available.toLocaleString()}
          </span>
          <span className="text-muted-foreground">Margine ~{wine.suggested_margin}%</span>
        </div>
      </div>
    </div>
  );
}
